<template>
    <div
        v-if="documentConfig"
        class="flex flex-col gap-3"
    >
        <DsMultiSelect
            v-model="selectedRecipients"
            :id="`document-upload-recipients-${instanceId}`"
            :ref="multiSelect"
            :error="documentConfig.mergedErrors.get(RECIPIENTS_ERROR)"
            label="Client(s)"
            :options="clientOptions"
            placeholder="Select client(s)"
            required
        >
            <template #optionLabel="{ option }">
                <ClientMultiSelectOption
                    :email="option.email"
                    :first="option.first"
                    :last="option.last"
                    :role="option.role"
                >
                    <template #trailing>
                        <EditClientButton :clientId="option.value" />
                    </template>
                </ClientMultiSelectOption>
            </template>
            <template #chipContent="{ option, truncate }">
                <span>
                    {{ truncate(getClientChipContent(option)) }}
                </span>
            </template>
            <template #emptyState>
                <NoClientsAdded
                    v-if="!clientOptions.length"
                    @addClients="handleAddClientClick"
                />
            </template>
        </DsMultiSelect>
    </div>
</template>

<script setup lang="ts">
import DsMultiSelect from '@/components/design-system/multiselect/DsMultiSelect.vue';
import EditClientButton from '@/components/Views/Company/AddFile/EditClient/EditClientButton.vue';
import ClientMultiSelectOption from '@/components/Views/Company/File/ClientMultiSelectOption.vue';
import { getClientChipContent } from '@/components/Views/Company/File/Documents/document-upload-utils';
import {
    DocumentUploadMergedErrorKeys,
    DocumentUploadValidationErrorKeys,
    type ClientOption,
    type DocumentUploadConfig,
} from '@/components/Views/Company/File/file.types';
import NoClientsAdded from '@/components/Views/Company/shared/NoClientsAdded.vue';
import { useFileDocumentStore } from '@/stores/file-document.store';
import { computed, useId, useTemplateRef, watch } from 'vue';

const { DOCUMENT_RECIPIENTS } = DocumentUploadValidationErrorKeys;
const { RECIPIENTS_ERROR } = DocumentUploadMergedErrorKeys;

const instanceId = useId();

const props = defineProps<{
    documentId: DocumentUploadConfig['documentId'];
    clientOptions: ClientOption[];
    documentConfig?: DocumentUploadConfig;
}>();

const emit = defineEmits<{
    (e: 'addClient'): void;
}>();

const fileDocumentStore = useFileDocumentStore();
const selectedRecipients = defineModel<ClientOption[]>('selectedRecipients', { default: [] });
const documentConfig = computed(() => fileDocumentStore.documentConfigs.get(props.documentId));

watch(selectedRecipients, () => {
    fileDocumentStore.clearDocumentErrorByKeys(props.documentId, [DOCUMENT_RECIPIENTS]);
    fileDocumentStore.updateDocument(props.documentId, {
        recipients: selectedRecipients.value.map(r => Number(r.id)),
    });
});

const multiSelect = useTemplateRef('multiSelect');
const handleAddClientClick = () => {
    // Clear focus from multiselect to prevent it from being covered by the add client sheet
    multiSelect.value?.handleOpen(false);
    // Emit event to let parent know scroll to the client section
    emit('addClient');
};
</script>
